import { ChatChip, CAPABILITIES_PROMPT } from '../work/ChipManager';

export const PERSONA_CHIP_STORAGE_KEY = 'tpp-persona-chat-chips';

// Default chip lineup, ordered roughly L→R by session arc:
//   intro → orientation → meet-the-mind → engagement → probing →
//   challenging → consulting → synthesizing → closing.
//
// Two chips intentionally absent from the chip bar but available in
// ComposeMenu's Conversation Actions: 🎯 Focus Session and 📝 Brief
// Next Person — situational, not daily-driver. 🪤 Failure Modes lives
// only in ComposeMenu (planning-flavored, not a 1-tap chip).
//
// New users get this 9-chip default. Existing users with stored chips
// pass through `loadPersonaChips` migration: known IDs get their labels
// updated only when the old default label matches (so customized labels
// are preserved), and the four new chips are appended to whatever the
// user already has. Demoted chips stay in storage — users can hide via
// ChipManager or hit "Reset to defaults" for the clean lineup.
export const DEFAULT_PERSONA_CHIPS: ChatChip[] = [
  {
    id: 'persona-capabilities',
    label: '🎭 Capabilities',
    prompt: CAPABILITIES_PROMPT,
    isDefault: true,
  },
  {
    id: 'persona-context-check',
    label: '🔍 Catch Me Up',
    prompt: `Tell me exactly what you currently know. Break it into:

1. **About me** — what you've picked up about who I am, how I work, what I care about
2. **This project/thread** — what we're working on and where we are
3. **Prior conversations** — anything you're carrying over from previous sessions
4. **Tools & skills loaded** — what capabilities are attached to this session right now
5. **Gaps** — what you *don't* know that would help you be more useful

Be honest about the edges of your context. If something is inferred vs. explicitly stated, say so.`,
    isDefault: true,
  },
  {
    id: 'persona-mental-model',
    label: '🧠 Imagine That?',
    prompt: `Walk me through how *you* think about this domain.

Cover:
1. **Heuristics** — the rules of thumb you actually use, not textbook platitudes
2. **Biases** — what you instinctively favor, and where that could lead you astray
3. **Hot takes** — the opinions you hold that a generic expert in this field wouldn't
4. **Signature moves** — the 2–3 techniques or framings you reach for first
5. **Blind spots** — what you're bad at or choose not to engage with

Be opinionated. Generic "it depends" answers are not useful here — I want the shape of your specific brain.`,
    isDefault: true,
  },
  {
    id: 'persona-hot-take',
    label: '⚡ Hot Take',
    prompt: `Give me your most opinionated, un-hedged take on whatever we've been discussing (or my last message if this is the first turn).

Rules:
- No disclaimers. No "it depends." No "well, some would argue..."
- Pick a side and defend it
- One paragraph of position, one paragraph of the strongest reason, one sentence on the best counter-argument and why you're not persuaded by it
- End with the specific action or decision your take implies

If you genuinely can't form a take because the question is too vague, tell me what single piece of context would unlock one.`,
    isDefault: true,
  },
  {
    id: 'persona-blind-spots',
    label: '❓ What Am I Missing?',
    prompt: `Look at what we've discussed in this thread and tell me — directly, no hedging — what important blind spots, missing perspectives, unstated assumptions, or unconsidered angles you can spot.

Format:
1. **Top 3 blind spots** with what each one costs me if I keep ignoring it
2. **One assumption I'm making** that's worth questioning
3. **One specific question** I haven't asked but should

Don't pad. Surface what actually matters. If our thread is too thin to spot anything substantive, ask me one targeted question that would let you see more clearly.`,
    isDefault: true,
  },
  {
    id: 'persona-socratic-probe',
    label: '🧐 Socratic Probe',
    prompt: `Apply the Socratic method to our most recent point. Ask me 3–5 sharp questions that drill into hidden assumptions, force precise definitions, or expose what I'm taking for granted.

Don't answer them — interrogate. End by naming the single question you'd want me to sit with the longest, and why that one.

If you're not sure what "our most recent point" should be probed, pick the one whose unexamined assumptions would cost me the most if they turn out to be wrong.`,
    isDefault: true,
  },
  {
    id: 'persona-second-opinion',
    label: '🤝 2nd Opinion',
    prompt: `Pick a peer with the right expertise from our roster (or ask me to pick if it's not clear) and have them weigh in on our most recent decision, idea, or claim.

Synthesize what they add:
1. **The most useful new perspective** they bring
2. **Where they push back** and what's behind that pushback
3. **Where they reinforce** — and whether that should make me more confident or just more comfortable
4. **What you'd do differently** based on their input — concretely

Use \`consult_peers\` if available; otherwise pick who you'd consult and reason out what they'd most likely say.`,
    isDefault: true,
  },
  {
    id: 'persona-visualize',
    label: '📊 Visualize',
    prompt: `Generate a Mermaid diagram that captures the key shape of what we've been discussing. You pick the type that best fits — flowchart / sequence / mindmap / pie / gantt / state / ER / classDiagram / timeline / xychart-beta — based on what's most clarifying for our content.

Make it scannable, not exhaustive — the goal is "I get it at a glance." Add a one-sentence reading guide above the diagram so I know what I'm looking at.

If our discussion doesn't have a natural shape yet, ask me which aspect to visualize before generating.`,
    isDefault: true,
  },
  {
    id: 'persona-wrap',
    label: '🏁 Wrap Up',
    prompt: `Wrap up this conversation into a shippable artifact. Produce:

**1. Decisions made** — bullet list, each with the decision + why
**2. Open questions** — bullet list, each with what's blocking a decision
**3. Next actions** — numbered list, each with owner (me, you, or "TBD") and rough timing
**4. Artifacts produced** — links to or inline copies of any deliverables we created
**5. One-line TL;DR** — for pasting into a status update

Format everything for clean copy-paste into a doc or ticket. No preamble, no trailing commentary — just the structured output.`,
    isDefault: true,
  },
];

// Rename map for the 2026-04-24 chip-surgery pass. Migration is polite:
// the label is only updated when the stored chip still has the OLD
// default label (so a user who customized "🧠 Mental Model" → "🧠 My
// Brain" doesn't get clobbered by the rename).
const CHIP_RENAME_MAP: Record<
  string,
  { matchOldLabel: string; newLabel: string }
> = {
  'persona-mental-model': {
    matchOldLabel: '🧠 Mental Model',
    newLabel: '🧠 Imagine That?',
  },
  'persona-context-check': {
    matchOldLabel: '🔍 Context Check',
    newLabel: '🔍 Catch Me Up',
  },
  'persona-handoff': {
    matchOldLabel: '📝 Handoff Note',
    newLabel: '📝 Brief Next Person',
  },
  'persona-wrap': {
    matchOldLabel: '🏁 Wrap & Ship',
    newLabel: '🏁 Wrap Up',
  },
};

export function loadPersonaChips(): ChatChip[] {
  try {
    const stored = localStorage.getItem(PERSONA_CHIP_STORAGE_KEY);
    if (stored) {
      const parsed: ChatChip[] = JSON.parse(stored);

      // Polite rename: only update label if the stored label still
      // matches the prior default. Custom user labels are preserved.
      let didChange = false;
      const renamed = parsed.map((c) => {
        const rename = CHIP_RENAME_MAP[c.id];
        if (rename && c.label === rename.matchOldLabel) {
          didChange = true;
          return { ...c, label: rename.newLabel };
        }
        return c;
      });

      // Append new default chips not present in storage. New chips land
      // at the end so the user's existing order is preserved.
      const existingIds = new Set(renamed.map((c) => c.id));
      const additions = DEFAULT_PERSONA_CHIPS.filter(
        (d) => !existingIds.has(d.id),
      );
      const final = additions.length
        ? [...renamed, ...additions.map((c) => ({ ...c }))]
        : renamed;
      if (additions.length) didChange = true;

      if (didChange) {
        try {
          localStorage.setItem(
            PERSONA_CHIP_STORAGE_KEY,
            JSON.stringify(final),
          );
        } catch {
          // localStorage unavailable — return migrated chips in-memory
          // anyway so the session sees the new lineup.
        }
      }
      return final;
    }
  } catch {
    // Stored value corrupt — fall through to defaults.
  }
  return DEFAULT_PERSONA_CHIPS.map((c) => ({ ...c }));
}

export function savePersonaChips(chips: ChatChip[]) {
  localStorage.setItem(PERSONA_CHIP_STORAGE_KEY, JSON.stringify(chips));
}
